import { useEffect } from "react";
import { geoDocumentAssets } from "@/theme/visualAssets";
import type { GeoAnalyticsThemeMode } from "@/theme/tokens";

type ThemedDocumentAssetsProps = {
  mode: GeoAnalyticsThemeMode;
};

function upsertLink(assetName: string, attributes: Record<string, string>) {
  const selector = `link[data-geo-document-asset="${assetName}"]`;
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  const link = existing ?? document.createElement("link");

  link.dataset.geoDocumentAsset = assetName;
  Object.entries(attributes).forEach(([attribute, value]) => {
    link.setAttribute(attribute, value);
  });

  if (!existing) {
    document.head.appendChild(link);
  }
}

function upsertMeta(assetName: string, attributes: Record<string, string>) {
  const selector = `meta[data-geo-document-asset="${assetName}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  const meta = existing ?? document.createElement("meta");

  meta.dataset.geoDocumentAsset = assetName;
  Object.entries(attributes).forEach(([attribute, value]) => {
    meta.setAttribute(attribute, value);
  });

  if (!existing) {
    document.head.appendChild(meta);
  }
}

export function ThemedDocumentAssets({ mode }: ThemedDocumentAssetsProps) {
  useEffect(() => {
    const assets = geoDocumentAssets[mode];

    upsertMeta("theme-color", {
      name: "theme-color",
      content: assets.themeColor
    });
    upsertMeta("ms-tile-color", {
      name: "msapplication-TileColor",
      content: assets.themeColor
    });
    upsertMeta("ms-tile-image", {
      name: "msapplication-TileImage",
      content: assets.tileImage
    });
    upsertMeta("ms-browserconfig", {
      name: "msapplication-config",
      content: assets.browserConfig
    });
    upsertLink("favicon-ico", {
      rel: "icon",
      type: "image/x-icon",
      href: assets.faviconIco
    });
    upsertLink("favicon-16", {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: assets.favicon16
    });
    upsertLink("favicon-32", {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: assets.favicon32
    });
    upsertLink("favicon-96", {
      rel: "icon",
      type: "image/png",
      sizes: "96x96",
      href: assets.favicon96
    });
    upsertLink("apple-touch-icon", {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: assets.appleTouchIcon
    });
  }, [mode]);

  return null;
}
